import { faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddBox from '@mui/icons-material/AddBox';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopy from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';
import Warning from '@mui/icons-material/Warning';
import {
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import { lightGreen, orange } from '@mui/material/colors';
import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '../../util/Configuracao';
import AutoComplete from '../AutoComplete';
import ReactComponent from '../ReactComponent';

class MovimentoCNJ extends ReactComponent {
    /** Movimento CNJ a ser incluido ou alterado */
    movimentoSelecionado;

    /** Valor do campo movimento CNJ */
    valorCampoMovimento = '';

    /** Movimentos CNJ cadastrados para o processo com os respectivos complementos */
    movimentosProcesso = [];

    /** Controle de exibição da dialog para inclusão/alteração de movimento e complementos */
    dialogMovimento = false;

    /** Índice do movimento aberto para alteração */
    indiceMovimentoAlteracao;

    /** Controle de loading durante salvamento */
    executandoAcao = false;

    movimentosGabinete = [];
    movimentosGabineteSelectedIds = [];

    confirmarImportacao = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            movimentoSelecionado: observable,
            valorCampoMovimento: observable,
            movimentosProcesso: observable,
            dialogMovimento: observable,
            indiceMovimentoAlteracao: observable,
            executandoAcao: observable,
            registro: computed,
            movimentosProcessuais: computed,
            recuperarMovimentosProcesso: action,
            opcoesMovimento: computed,
            descricaoOpcaoMovimentoSelecionado: computed,
            callbackAutoCompleteMovimento: action,
            configurarValorPadraoClasseProcessual: action,
            handleOpenDialog: action,
            handleCloseDialog: action,
            handleClickRemoverMovimento: action,
            handleClickCancelar: action,
            handleClickBotaoConfirmar: action,
            handleClickAbrirMovimento: action,
            gravarMovimentosComComplementos: action,
            movimentoSelecionadoPossuiComplementos: computed,
            listaComplementos: computed,
            callbackAutoCompleteComplemento: action,
            callbackInputComplementoChange: action,
            opcoesComplementoDinamico: computed,
            opcoesNomeParte: computed,
            opcoesClasseProcessual: computed,
            classesProcesso: computed,
            alteracaoPermitida: computed,
            movimentosGabinete: observable,
            recuperarMovimentosGabinete: action,
            confirmarImportacao: observable,
            importarMovimentosSelecionados: action,
            setConfirmarImportacao: action,
            possuiMovimentosAdicionais: computed,
            movimentosGabineteSelectedIds: observable,
            movimentosGabineteSelecionados: computed,
            toggleMovimentoSelecionado: action,
            movimentosGabineteValidos: computed
        });

        this.createReaction(
            reaction(
                () => this.registro,
                () => {
                    this.recuperarMovimentosProcesso();
                    this.recuperarMovimentosGabinete();
                }
            )
        );
        // Atualiza o valor do campo sempre que o resultado de julgamento for alterado
        this.createReaction(
            reaction(
                () => this.registro?.resultadoJulgamento?.codigo,
                () => {
                    if (this.registro.processo.origem === Configuracao.ORIGEM_PJE) {
                        this.recuperarMovimentosProcesso();
                        this.recuperarMovimentosGabinete();
                    }
                }
            )
        );
        this.createReaction(
            reaction(
                () => this.movimentoSelecionado,
                async () => {
                    if (this.movimentoSelecionado) {
                        let dominios = await this.dominioStore.recuperarDominiosComplementos(
                            parseInt(this.movimentoSelecionado.numeroMovimentoCnj, 10)
                        );
                        if (dominios) {
                            runInAction(() => {
                                if (this.movimentoSelecionado) {
                                    this.movimentoSelecionado.dominiosComplementos = dominios;
                                }
                            });
                        }
                    }
                }
            )
        );

        this.recuperarMovimentosProcesso();
        this.recuperarMovimentosGabinete();
    }

    get registro() {
        return this.observableProps.registro;
    }

    get movimentosProcessuais() {
        return this.dominioStore.movimentosProcessuaisPadronizados.filter(m => m.ativo);
    }

    recuperarMovimentosProcesso = async () => {
        let movimentos;
        try {
            if (this.registro) {
                movimentos = await this.processoService.recuperarMovimentosProcesso(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto
                );
            } else {
                movimentos = this.observableProps.movimentos;
            }
            if (movimentos && movimentos.length > 0) {
                movimentos = movimentos.sort((m1, m2) => (m1.numeroOrdemMovimento < m2.numeroOrdemMovimento ? -1 : 1));
                for (let index in movimentos) {
                    await this.dominioStore.complementarInformacoesMovimento(movimentos[index]);
                }
            }
        } finally {
            runInAction(() => {
                if (this.registro) {
                    this.registro.detalhes.movimentos = movimentos;
                }
                this.movimentosProcesso = movimentos || [];
            });
        }
    };

    recuperarMovimentosGabinete = async () => {
        let movimentos;
        try {
            if (this.registro) {
                movimentos = await this.processoService.recuperarMovimentosGabinete(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto
                );
            } else {
                movimentos = this.observableProps.movimentosGabinete;
            }
            if (movimentos && movimentos.length > 0) {
                movimentos = movimentos.sort((m1, m2) => (m1.numeroOrdemMovimento < m2.numeroOrdemMovimento ? -1 : 1));
                for (let index in movimentos) {
                    await this.dominioStore.complementarInformacoesMovimento(movimentos[index]);
                }
            }
        } finally {
            runInAction(() => {
                movimentos = (movimentos || []).filter((m) => m.descricao != null && m.descricao !== '');
                if (this.registro) {
                    this.registro.detalhes.movimentosGabinete = movimentos;
                }
                this.movimentosGabinete = movimentos || [];
            });
        }
    };

    // Movimento originário da lista de movimentos do cnj, a qual possui todas as opções de complemento para cada movimento
    movimentoOriginal = (numeroMovimento) => {
        return this.movimentosProcessuais.find((movimento) => movimento.numeroMovimentoCnj === numeroMovimento);
    };

    get opcoesMovimento() {
        return this.movimentosProcessuais ? this.converterOpcoesMovimento(this.movimentosProcessuais) : [];
    }

    converterOpcoesMovimento = (movimentos) => {
        return movimentos.map((movimento) => ({
            texto: movimento.numeroMovimentoCnj + ' - ' + movimento.descricao,
            valor: movimento.numeroMovimentoCnj.toString()
        }));
    };

    get descricaoOpcaoMovimentoSelecionado() {
        let opcao;
        if (this.movimentoSelecionado) {
            opcao = this.opcoesMovimento.find((opcao) => {
                return this.movimentoSelecionado.numeroMovimentoCnj === parseInt(opcao.valor, 10);
            });
        }
        return opcao ? opcao.texto : '';
    }

    callbackAutoCompleteMovimento = (option) => {
        if (option && option.valor) {
            let numeroMovimento = parseInt(option.valor, 10);
            if (
                !this.movimentoSelecionado ||
                parseInt(this.movimentoSelecionado.numeroMovimentoCnj, 10) !== numeroMovimento
            ) {
                this.movimentoSelecionado = this.movimentoOriginal(numeroMovimento);
                this.configurarValorPadraoClasseProcessual();
            }
            this.valorCampoMovimento = this.descricaoOpcaoMovimentoSelecionado;
        }
    };

    configurarValorPadraoClasseProcessual() {
        if (this.movimentoSelecionadoPossuiComplementos) {
            let complementoClasse = this.movimentoSelecionado.complementos.find(
                (complemento) => complemento.codigoComplementoCnj === Configuracao.COD_COMPLEMENTO_CLASSE
            );
            if (complementoClasse && this.registro) {
                complementoClasse.valor = this.registro.classeProcesso;
            }
        }
    }

    handleOpenDialog = () => {
        this.dialogMovimento = true;
    };

    handleCloseDialog = async () => {
        this.dialogMovimento = false;
        this.movimentoSelecionado = undefined;
        this.indiceMovimentoAlteracao = undefined;
        this.valorCampoMovimento = '';
        this.recuperarMovimentosProcesso();
        this.recuperarMovimentosGabinete();
    };

    handleClickRemoverMovimento = async (numeroMovimento, numeroOrdemMovimento) => {
        this.movimentosProcesso.splice(
            this.movimentosProcesso.findIndex(
                (movimento) =>
                    movimento.numeroMovimentoCnj === numeroMovimento &&
                    movimento.numeroOrdemMovimento === numeroOrdemMovimento
            ),
            1
        );
        await this.gravarMovimentosComComplementos();
    };

    handleClickCancelar = async () => {
        await this.handleCloseDialog();
    };

    handleClickBotaoConfirmar = async () => {
        this.executandoAcao = true;
        try {
            if (this.indiceMovimentoAlteracao === undefined) {
                await this.incluirMovimento();
            } else {
                await this.alterarMovimento();
            }
        } finally {
            runInAction(() => {
                this.executandoAcao = false;
            });
        }
    };

    async incluirMovimento() {
        if (this.validarInclusaoMovimento()) {
            this.movimentosProcesso.push(this.movimentoSelecionado);
            await this.gravarMovimentosComComplementos();
            await this.handleCloseDialog();
        } else {
            this.mensagemStore.abrirMensagemAlerta(
                'O movimento ' +
                    this.movimentoSelecionado.numeroMovimentoCnj +
                    ' já foi íncluído' +
                    (this.movimentoSelecionadoPossuiComplementos ? ' com o(s) complemento(s) selecionado(s).' : '.')
            );
        }
    }

    validarInclusaoMovimento() {
        let movimentosProcesso = this.movimentosProcesso.filter(
            (mov) => mov.numeroMovimentoCnj === this.movimentoSelecionado.numeroMovimentoCnj
        );
        return this.validarMovimentosComplementos(movimentosProcesso);
    }

    validarMovimentosComplementos(movimentosProcesso) {
        let retorno = true;
        if (movimentosProcesso && movimentosProcesso.length > 0) {
            if (this.movimentoSelecionadoPossuiComplementos) {
                movimentosProcesso.forEach((movimentoProcesso) => {
                    if (this.todosComplementosIguais(movimentoProcesso, this.movimentoSelecionado)) {
                        // Achou um movimento cadastrado para o processo com os mesmos complementos selecionados
                        retorno = false;
                    }
                });
            } else {
                // Achou um movimento cadastrado para o processo igual ao selecionado e ambos não tem complementos
                retorno = false;
            }
        }
        return retorno;
    }

    todosComplementosIguais(movimentoAnterior, movimentoNovo) {
        if (movimentoNovo.complementos && movimentoNovo.complementos.length > 0) {
            for (let complementoNovo of movimentoNovo.complementos) {
                let indiceComplementoDiferente = movimentoAnterior.complementos.findIndex(
                    (complementoAnterior) =>
                        complementoNovo.codigoComplementoCnj === complementoAnterior.codigoComplementoCnj &&
                        (complementoNovo.tipo === Configuracao.TIPO_COMPLEMENTO_DOMINIO
                            ? complementoNovo.codigoDominioComplementoCnj !==
                              complementoAnterior.codigoDominioComplementoCnj
                            : complementoNovo.valor !== complementoAnterior.valor)
                );
                if (indiceComplementoDiferente !== -1) {
                    return false;
                }
            }
        }
        return true;
    }

    async alterarMovimento() {
        if (this.validarAlteracaoMovimento()) {
            this.movimentosProcesso[this.indiceMovimentoAlteracao] = this.movimentoSelecionado;
            await this.gravarMovimentosComComplementos();
            await this.handleCloseDialog();
        } else {
            this.mensagemStore.abrirMensagemAlerta(
                'O movimento ' +
                    this.movimentoSelecionado.numeroMovimentoCnj +
                    ' já foi íncluído' +
                    (this.movimentoSelecionadoPossuiComplementos ? ' com o(s) complemento(s) selecionado(s).' : '.')
            );
        }
    }

    validarAlteracaoMovimento() {
        let movimentosProcesso;
        if (
            this.movimentoSelecionado.numeroMovimentoCnj ===
            this.movimentosProcesso[this.indiceMovimentoAlteracao].numeroMovimentoCnj
        ) {
            movimentosProcesso = this.movimentosProcesso.filter(
                (mov, index) =>
                    index !== this.indiceMovimentoAlteracao &&
                    mov.numeroMovimentoCnj === this.movimentoSelecionado.numeroMovimentoCnj
            );
        } else {
            movimentosProcesso = this.movimentosProcesso.filter(
                (mov) => mov.numeroMovimentoCnj === this.movimentoSelecionado.numeroMovimentoCnj
            );
        }
        return this.validarMovimentosComplementos(movimentosProcesso);
    }

    handleClickAbrirMovimento = (movimento, index) => {
        this.indiceMovimentoAlteracao = index;
        this.movimentoSelecionado = movimento;
        this.valorCampoMovimento = this.descricaoOpcaoMovimentoSelecionado;
        this.handleOpenDialog();
    };

    gravarMovimentosComComplementos = async () => {
        let ordem = 1;
        let movimentos = this.movimentosProcesso.map((movimento) => ({
            numeroMovimentoCnj: movimento.numeroMovimentoCnj,
            numeroOrdemMovimento: ordem++,
            complementos: this.converterComplementosParaGravar(movimento)
        }));
        if (this.registro) {
            // O serviço apaga todos os movimentos e grava o que foi passado, sendo necessário enviar todos os movimentos com complementos
            await this.processoService.gravarMovimentos(
                this.registro.sessaoId,
                this.registro.processo.numeroCompleto,
                movimentos
            );
            runInAction(() => {
                this.registro.movimentosCadastrados = this.movimentosProcesso.length > 0;
                this.registro.informacoesAdicionais.quantidadeMovimentosCadastrados = this.movimentosProcesso.length;
            });
        } else if (this.observableProps.callbackMovimentosSelecionados) {
            this.observableProps.callbackMovimentosSelecionados(movimentos);
        }
    };

    converterComplementosParaGravar(movimento) {
        let retorno = [];
        let numeroOrdem = 1;

        if (movimento.complementos && movimento.complementos.length > 0) {
            movimento.complementos.forEach((complemento) => {
                if (this.isComplementoPreenchido(complemento)) {
                    let complementoGravar = {
                        codigoComplementoCnj: complemento.codigoComplementoCnj,
                        numeroOrdem: numeroOrdem++
                    };

                    if (complemento.codigoDominioComplementoCnj !== undefined) {
                        complementoGravar['codigoDominioComplementoCnj'] = complemento.codigoDominioComplementoCnj;
                    } else {
                        complementoGravar['valor'] = complemento.valor.replace(/ \([^)]+\)$/, '');
                    }

                    retorno.push(complementoGravar);
                }
            });
        }
        return retorno;
    }

    isComplementoPreenchido(complemento) {
        return (
            complemento &&
            ((complemento.valor && complemento.valor !== null) || complemento.codigoDominioComplementoCnj !== undefined)
        );
    }

    get movimentoSelecionadoPossuiComplementos() {
        return this.movimentoSelecionado?.complementos && this.movimentoSelecionado.complementos.length > 0;
    }

    get listaComplementos() {
        return this.movimentoSelecionadoPossuiComplementos ? this.movimentoSelecionado.complementos : [];
    }

    callbackAutoCompleteComplemento = (complemento, option) => {
        let valor = option && option.valor;
        if (complemento.tipo === Configuracao.TIPO_COMPLEMENTO_DOMINIO) {
            complemento.codigoDominioComplementoCnj = valor ? parseInt(valor, 10) : undefined;
        } else {
            complemento.valor = valor;
        }
        // Força a renderização dos complementos para atualizar os campos com complemento excludente desabilitados
        this.movimentoSelecionado.complementos = this.movimentoSelecionado.complementos.slice();
        console.log('this.movimentoSelecionado.complementos', toJS(this.movimentoSelecionado.complementos));
    };

    callbackInputComplementoChange = (complemento, value) => {
        if (this.permitirQualquerValor(complemento)) {
            complemento.valor = value;
        }
    };

    descricaoComplemento = (movimento, complemento) => {
        return this.dominioStore.descricaoComplemento(movimento, complemento);
    };

    opcoesComplemento = (complemento) => {
        let opcoes;

        if (complemento.tipo === Configuracao.TIPO_COMPLEMENTO_DOMINIO) {
            opcoes = this.opcoesDominio(complemento.nome);
        } else if (complemento.tipo === Configuracao.TIPO_COMPLEMENTO_DINAMICO) {
            opcoes = this.opcoesComplementoDinamico[complemento.codigoComplementoCnj];
        }

        return opcoes || [];
    };

    opcoesDisable(complemento) {
        return this.opcoesComplemento(complemento).filter(
            (complemento) =>
                complemento.valor === '7198' ||
                complemento.valor === '7199' ||
                complemento.valor === '7200' ||
                complemento.valor === '7202' ||
                complemento.valor === '7201'
        );
    }

    opcoesDominio(nomeDominioComplemento) {
        let dominiosComplementos = this.movimentoSelecionado.dominiosComplementos;
        let dominioComplemento = dominiosComplementos.filter(
            (dominioComplemento) => dominioComplemento.dominio === nomeDominioComplemento
        );
        return dominioComplemento
            ? dominioComplemento.map((dominio) => ({ texto: dominio.valor, valor: dominio.codigo.toString() }))
            : [];
    }

    get opcoesComplementoDinamico() {
        return {
            [Configuracao.COD_COMPLEMENTO_PARTE]: this.opcoesNomeParte,
            [Configuracao.COD_COMPLEMENTO_CLASSE]: this.opcoesClasseProcessual
        };
    }

    get opcoesNomeParte() {
        let opcoes = [];

        if (this.registro?.detalhes?.partesProcessuais) {
            this.registro.detalhes.partesProcessuais.forEach((parteProcessual) =>
                parteProcessual.partes.forEach((parte) => {
                    if (opcoes.findIndex((opcao) => opcao.codigoParte === parte.parte.codigo) === -1) {
                        opcoes.push({ texto: parte.parte.nome, valor: parte.parte.nome, codigoParte: parte.parte.codigo });
                    }
                })
            );
        }
        return opcoes;
    }

    get opcoesClasseProcessual() {
        return this.classesProcesso.map((classe) => ({
            texto: classe.sigla + ' - ' + classe.descricao,
            valor: classe.sigla
        }));
    }

    get classesProcesso() {
        return this.dominioStore.getClassesProcesso(Configuracao.ORIGEM_PJE) || [];
    }

    tooltipMovimento = (movimento) => {
        if (movimento) {
            let descricaoMovimento = movimento.descricao
                ? this.corrigirString(movimento.descricao)
                : '<span style="color: orange">"O movimento ' +
                  movimento.numeroMovimentoCnj +
                  ' não está cadastrado na Secretaria Eletrônica"</span>';
            if (movimento.complementos && movimento.complementos.length > 0) {
                descricaoMovimento = this.dominioStore.substituirDescricoesGruposComplemento(
                    descricaoMovimento,
                    movimento
                );
                descricaoMovimento = this.dominioStore.substituirDescricoesComplementos(
                    descricaoMovimento,
                    movimento,
                    movimento.complementos
                );
            }
            return (
                <div
                    style={{ fontSize: '14px' }}
                    dangerouslySetInnerHTML={{ __html: movimento.numeroMovimentoCnj + ' - ' + descricaoMovimento }}
                />
            );
        }
    };

    renderIconeComplemento = (movimento) => {
        if (movimento?.complementos && movimento.complementos.length > 0) {
            return (
                <FontAwesomeIcon
                    icon={faAngleDoubleRight}
                    color={this.todosComplementosPreenchidos(movimento) ? lightGreen[900] : orange[900]}
                    style={{ fillOpacity: '0.7', paddingLeft: '10px', width: '16px', height: '16px' }}
                />
            );
        }
    };

    todosComplementosPreenchidos(movimento) {
        let retorno = true;
        for (let index in movimento.complementos.slice()) {
            let complemento = movimento.complementos[index];
            if (retorno && !this.isComplementoPreenchido(complemento)) {
                retorno =
                    complemento.complementosExcludentes &&
                    complemento.complementosExcludentes.findIndex((complementoExcludente) =>
                        this.isComplementoPreenchido(this.getComplemento(movimento, complementoExcludente))
                    ) > -1;
            }
        }
        return retorno;
    }

    permitirQualquerValor(complemento) {
        return complemento.tipo === Configuracao.TIPO_COMPLEMENTO_LIVRE;
    }

    isComplementoHabilitado(complemento) {
        if (!this.alteracaoPermitida) {
            return false;
        }
        if (complemento.complementosExcludentes && complemento.complementosExcludentes.length > 0) {
            return (
                complemento.complementosExcludentes.findIndex((complementoExcludente) => {
                    let complementoComValor = this.getComplemento(this.movimentoSelecionado, complementoExcludente);
                    return this.isComplementoPreenchido(complementoComValor);
                }) === -1
            );
        }
        return true;
    }

    get alteracaoPermitida() {
        return !this.registro?.movimentosTransferidos;
    }

    handleCopyNumeroMovimentoCnj = () => {
        const numeros = this.movimentosProcesso.map((m) => m.numeroMovimentoCnj).join(', ');
        navigator.clipboard.writeText(numeros);
        this.mensagemStore.abrirMensagemSucesso('Números de movimento copiados para a área de transferência');
    };

    getComplemento(movimento, complemento) {
        return movimento.complementos.find((compl) => compl.codigoComplementoCnj === complemento.codigoComplementoCnj);
    }

    tooltipComplemento(complemento) {
        return this.alteracaoPermitida &&
            !this.isComplementoHabilitado(complemento) &&
            complemento.complementosExcludentes &&
            complemento.complementosExcludentes.length > 0
            ? 'É necessário informar apenas um dos seguintes campos: ' +
                  this.corrigirString(complemento.nome).toUpperCase() +
                  ', ' +
                  complemento.complementosExcludentes.map(
                      (compl, index) =>
                          compl.nome.toUpperCase() +
                          (index < complemento.complementosExcludentes.length - 1 ? ', ' : '')
                  )
            : '';
    }

    setConfirmarImportacao(valor) {
        this.confirmarImportacao = valor;
    }

    get possuiMovimentosAdicionais() {
        const movimentosGE = this.movimentosGabinete ?? [];
        const movimentosSE = this.movimentosProcesso ?? [];
        if (movimentosGE.length === 0) {
            return false;
        }
        if (movimentosSE.length === 0) {
            return true;
        }
        return this.filtrarMovimentosSomenteGabinete(movimentosGE, movimentosSE).length > 0;
    }

    get normalizadoresPorComplemento() {
        return {
            [Configuracao.COD_COMPLEMENTO_PARTE]: (valor) => (valor ?? '').replace(/ \(polo \w+\)$/i, '').trim(),
            [Configuracao.COD_COMPLEMENTO_CLASSE]: (valor) => {
                if (!valor) return '';
                const classe = this.classesProcesso.find((c) => c.sigla === valor || c.descricao === valor);
                return classe ? classe.codigo : valor;
            }
        };
    }

    isMesmoComplemento = (complementoGE, complementoSE) => {
        const codigoComplementoGE = complementoGE.codigoComplementoCnj;
        const codigoComplementoSE = complementoSE.codigoComplementoCnj;

        if (codigoComplementoGE !== codigoComplementoSE) {
            return false;
        }

        const dominioGE = complementoGE.codigoDominioComplementoCnj;
        const dominioSE = complementoSE.codigoDominioComplementoCnj;
        if (dominioGE != null && dominioSE != null) {
            return dominioGE === dominioSE;
        }

        const normalizar = this.normalizadoresPorComplemento[codigoComplementoGE] ?? ((valor) => valor);
        return normalizar(complementoGE.valor) === normalizar(complementoSE.valor);
    };

    filtrarMovimentosSomenteGabinete(movimentosGE, movimentosSE) {
        const isMesmoMovimento = (movimentoGE, movimentoSE) => {
            if (movimentoGE.numeroMovimentoCnj !== movimentoSE.numeroMovimentoCnj) {
                return false;
            }

            const complementosGE = movimentoGE.complementos || [];
            const complementosSE = movimentoSE.complementos || [];

            if (complementosGE.length !== complementosSE.length) {
                return false;
            }

            if (complementosGE.length === 0 && complementosSE.length === 0) {
                return true;
            }

            return complementosGE.every((complementoGE, index) => {
                const complementoSE = complementosSE[index];
                return this.isMesmoComplemento(complementoGE, complementoSE);
            });
        };

        return movimentosGE.filter(
            (movimentoGE) => !movimentosSE.some((movimentoSE) => isMesmoMovimento(movimentoGE, movimentoSE))
        );
    }

    importarMovimentosSelecionados = async () => {
        const novosMovimentos = this.movimentosGabineteSelecionados;
        this.movimentosProcesso.push(...novosMovimentos);
        this.confirmarImportacao = false;
        this.executandoAcao = true;
        try {
            await this.gravarMovimentosComComplementos();
        } finally {
            this.executandoAcao = false;
            this.movimentosGabineteSelectedIds = [];
        }
    };

    getMovimentoId = (movimento) => `${movimento.numeroMovimentoCnj}-${movimento.numeroOrdemMovimento}`;

    get movimentosGabineteSelecionados() {
        return this.movimentosGabinete.filter((mov) =>
            this.movimentosGabineteSelectedIds.includes(this.getMovimentoId(mov))
        );
    }

    toggleMovimentoSelecionado = (idSelecionado) => {
        if (this.movimentosGabineteSelectedIds.includes(idSelecionado)) {
            this.movimentosGabineteSelectedIds = this.movimentosGabineteSelectedIds.filter(
                (id) => id !== idSelecionado
            );
        } else {
            this.movimentosGabineteSelectedIds.push(idSelecionado);
        }
    };

    get movimentosGabineteValidos() {
        return this.filtrarMovimentosSomenteGabinete(this.movimentosGabinete, this.movimentosProcesso);
    }

    render() {
        return (
            <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                <Typography
                    variant="subtitle1"
                    color="primary"
                    style={{ marginLeft: '8px', whiteSpace: 'nowrap', textAlign: 'center' }}
                >
                    <b>{'Movimento CNJ'}</b>
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {this.alteracaoPermitida && (
                        <IconButton
                            onClick={this.handleOpenDialog}
                            size="large"
                            id="iconeAdicionarMovimento"
                        >
                            <Tooltip title="Adicionar Movimento">
                                <AddBox />
                            </Tooltip>
                        </IconButton>
                    )}
                    {this.movimentosProcesso && this.movimentosProcesso.length > 0 && (
                        <IconButton
                            onClick={this.handleCopyNumeroMovimentoCnj}
                            size="large"
                        >
                            <Tooltip title="Copiar Números dos Movimentos">
                                <ContentCopy />
                            </Tooltip>
                        </IconButton>
                    )}
                    {!this.alteracaoPermitida && !(this.movimentosProcesso && this.movimentosProcesso.length > 0) && (
                        <span style={{ marginLeft: '20px' }} />
                    )}
                </div>
                <div
                    style={{
                        height: '50px',
                        width: '100%',
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderRadius: '4px',
                        padding: '5px'
                    }}
                >
                    {this.movimentosProcesso &&
                        this.movimentosProcesso.length > 0 &&
                        this.movimentosProcesso.map((movimento, index) => (
                            <Tooltip
                                key={'ttk' + index}
                                title={this.tooltipMovimento(movimento)}
                                placement="top"
                            >
                                <Chip
                                    key={'mov' + index}
                                    label={
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ fontSize: '14px' }}>{movimento.numeroMovimentoCnj}</span>
                                            {this.renderIconeComplemento(movimento)}
                                        </div>
                                    }
                                    onDelete={
                                        this.alteracaoPermitida
                                            ? async () =>
                                                  await this.handleClickRemoverMovimento(
                                                      movimento.numeroMovimentoCnj,
                                                      movimento.numeroOrdemMovimento
                                                  )
                                            : undefined
                                    }
                                    onClick={() => this.handleClickAbrirMovimento(movimento, index)}
                                    style={{ margin: '2px', height: '30px', color: 'black', fontSize: 12 }}
                                />
                            </Tooltip>
                        ))}
                    {this.possuiMovimentosAdicionais && (
                        <Tooltip title="Movimentos cadastrados no gabinete eletrônico, clique aqui para importar">
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Warning
                                    onClick={() => this.setConfirmarImportacao(true)}
                                    style={{
                                        fill: 'darkorange',
                                        cursor: 'pointer',
                                        width: '22px',
                                        height: '22px',
                                        transform: 'translateY(6px)'
                                    }}
                                />
                            </div>
                        </Tooltip>
                    )}
                    <Dialog
                        open={this.confirmarImportacao}
                        onClose={() => this.setConfirmarImportacao(false)}
                    >
                        <DialogTitle>Importar movimentos do Gabinete Eletrônico</DialogTitle>
                        <DialogContent>
                            {
                                <div
                                    style={{
                                        maxHeight: 300,
                                        overflowY: 'auto',
                                        marginTop: 8,
                                        border: '1px solid rgba(0,0,0,0.23)',
                                        borderRadius: 4,
                                        padding: 8
                                    }}
                                >
                                    {this.movimentosGabineteValidos.map((movimento) => {
                                        const id = this.getMovimentoId(movimento);
                                        const checked = this.movimentosGabineteSelectedIds.includes(id);
                                        return (
                                            <FormControlLabel
                                                key={id}
                                                control={
                                                    <Checkbox
                                                        checked={checked}
                                                        onChange={() => this.toggleMovimentoSelecionado(id)}
                                                    />
                                                }
                                                label={
                                                    <span style={{ fontSize: 14 }}>
                                                        {this.tooltipMovimento(movimento)}
                                                    </span>
                                                }
                                                style={{ display: 'flex' }}
                                            />
                                        );
                                    })}
                                </div>
                            }
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                onClick={() => this.setConfirmarImportacao(false)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={this.importarMovimentosSelecionados}
                                disabled={this.movimentosGabineteSelectedIds.length === 0}
                            >
                                Importar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
                {this.dialogMovimento && (
                    <Dialog
                        fullWidth
                        maxWidth="md"
                        open={this.dialogMovimento}
                        onClose={this.handleCloseDialog}
                    >
                        <DialogContent style={{ display: 'flex', flexDirection: 'column', padding: '0px' }}>
                            <div style={{ minWidth: '550px', padding: '20px 25px 20px 20px' }}>
                                <AutoComplete
                                    autoFocus={!this.valorCampoMovimento}
                                    label="Movimento CNJ"
                                    buscaMultivalorada={true}
                                    title={this.descricaoOpcaoMovimentoSelecionado}
                                    value={this.valorCampoMovimento || null}
                                    options={this.opcoesMovimento}
                                    onChange={this.callbackAutoCompleteMovimento}
                                    disabled={!this.alteracaoPermitida}
                                />
                                {this.movimentoSelecionadoPossuiComplementos && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                style={{ color: '#01579B', fontSize: '11pt' }}
                                                variant="subtitle1"
                                            >
                                                Complementos
                                            </Typography>
                                            <div>
                                                <IconButton
                                                    color="inherit"
                                                    aria-label="info"
                                                    style={{ width: '50px', cursor: 'default' }}
                                                    size="large"
                                                >
                                                    <Tooltip
                                                        title={
                                                            'Alguns complementos estão desabilitados para evitar incoerências no sistema.'
                                                        }
                                                    >
                                                        <InfoIcon style={{ color: '#01579b' }} />
                                                    </Tooltip>
                                                </IconButton>
                                            </div>
                                        </div>
                                        {this.listaComplementos?.map((complemento, index) => (
                                            <div
                                                key={index + '' + complemento.codigoComplementoCnj}
                                                style={{ display: 'flex' }}
                                            >
                                                <Tooltip
                                                    title={this.tooltipComplemento(complemento)}
                                                    placement="bottom-start"
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            marginTop: '5px',
                                                            marginBottom: '8px'
                                                        }}
                                                    >
                                                        <AutoComplete
                                                            permitirQualquerValor={this.permitirQualquerValor(
                                                                complemento
                                                            )}
                                                            label={
                                                                complemento.label
                                                                    ? complemento.label.toUpperCase()
                                                                    : complemento.valor.toUpperCase()
                                                            }
                                                            options={this.opcoesComplemento(complemento)}
                                                            value={
                                                                this.descricaoComplemento(
                                                                    this.movimentoSelecionado,
                                                                    complemento
                                                                ) || null
                                                            }
                                                            onChange={(option) =>
                                                                this.callbackAutoCompleteComplemento(
                                                                    complemento,
                                                                    option
                                                                )
                                                            }
                                                            onInputChange={(event, value) =>
                                                                this.callbackInputComplementoChange(complemento, value)
                                                            }
                                                            disabled={!this.isComplementoHabilitado(complemento)}
                                                            getOptionDisabled={this.opcoesDisable(complemento)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                            {this.alteracaoPermitida ? (
                                <DialogActions>
                                    <Button
                                        onClick={this.handleClickCancelar}
                                        color="secondary"
                                        variant="outlined"
                                        endIcon={<CancelIcon />}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={this.handleClickBotaoConfirmar}
                                        color="primary"
                                        variant="outlined"
                                        endIcon={<SaveIcon />}
                                        loading={this.executandoAcao}
                                        disabled={!this.movimentoSelecionado || this.executandoAcao}
                                    >
                                        Salvar
                                    </Button>
                                </DialogActions>
                            ) : (
                                <DialogActions>
                                    <Button
                                        onClick={this.handleClickCancelar}
                                        color="primary"
                                        variant="contained"
                                        endIcon={<ThumbUpAlt />}
                                    >
                                        Ok
                                    </Button>
                                </DialogActions>
                            )}
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        );
    }
}

export default inject('stores')(observer(MovimentoCNJ));
